// ================================================================
// src/pages/ParentHome.jsx — نسخه‌ی نهایی اصلاح‌شده
// ================================================================
// ✅ Privacy-First: تمام داده‌ها با childId (id-student) خوانده می‌شوند
// ✅ منبع اصلی فرزند: فیلدهای -student روی آبجکت والد
// ✅ Enrichment از users array برای داده‌های تازه (سکه، XP، آواتار)
// ✅ حذف کامل self-view fallback (مشکل اصلی)
// ✅ Live Study: با StudyTimer یکپارچه (study_active_*)
// ✅ پیام‌های والد: قابل مشاهده در StudentHome (parent-messages-*)
// ✅ آواتار: هم تصویر و هم ایموجی
// ✅ درس‌ها: از COURSE_DETAILS واقعی
// ✅ تم: با localStorage persist + دکمه‌ی شناور
// ✅ مدال‌ها: از ALL_DEFINITIONS واقعی (با fallback امن)
// ✅ Empty states، Skeleton، Toast، RTL کامل
// ================================================================
// 🆕 FIX: دکمه‌ی «گزارش کامل» در کارت نمرات اخیر حالا childId را
//         در URL می‌فرستد تا ParentDashboard داده‌ی فرزند را نشان دهد
// ================================================================

import React, {
  useState, useEffect, useCallback, useMemo,
} from 'react';
import { useNavigate } from 'react-router-dom';
import './ParentHome.css';
import { useAuth } from '../context/AuthContext';
import { useExams, toNumber } from '../components/Exams/ExamContext.jsx';
import { COURSE_DETAILS } from './CourseDetail';
import { engine } from '../core/books/storage.js';   // 🆕 همه‌چیز در DB
import {
  normalizeGradeId,
  normalizeFieldId,
  getSubject as getSubjectMeta,
  getGrade as getGradeMeta,
  getField as getFieldMeta,
  needsField as curriculumNeedsField,
} from '../core/education/curriculum.js';

// تلاش برای import امن مدال‌ها
let ALL_MEDAL_DEFS = [];
try {
  // eslint-disable-next-line
  const medalsModule = require('../data/medalsDefinitions');
  ALL_MEDAL_DEFS = medalsModule?.ALL_DEFINITIONS || [];
} catch (e) {
  ALL_MEDAL_DEFS = [];
}

/* ================================================================
   ۰۱. HELPERS
   ================================================================ */
const toPersianDigits = (n) =>
  String(n ?? '').replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d]);

const getTodayKey = () => new Date().toISOString().split('T')[0];
const getYesterdayKey = () =>
  new Date(Date.now() - 86400000).toISOString().split('T')[0];
const getPersianDayIndex = () => (new Date().getDay() + 1) % 7;

const safeRead = (key, fallback) => {
  try {
    return engine.get(key, fallback);
  } catch { return fallback; }
};

const safeReadNum = (key, fallback = 0) => {
  try {
    const v = engine.get(key, null);
    if (v == null) return fallback;
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  } catch { return fallback; }
};

const timeAgo = (ts) => {
  if (!ts) return '';
  const mins = Math.floor((Date.now() - ts) / 60000);
  if (mins < 1) return 'همین الان';
  if (mins < 60) return `${toPersianDigits(mins)} دقیقه پیش`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${toPersianDigits(h)} ساعت پیش`;
  return `${toPersianDigits(Math.floor(h / 24))} روز پیش`;
};

const formatMinutes = (mins) => {
  if (!mins || mins <= 0) return '۰';
  if (mins < 60) return `${toPersianDigits(mins)} د`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (m === 0) return `${toPersianDigits(h)} س`;
  return `${toPersianDigits(h)}:${toPersianDigits(String(m).padStart(2, '0'))}`;
};

const formatMinutesLong = (mins) => {
  if (!mins || mins <= 0) return '۰ دقیقه';
  if (mins < 60) return `${toPersianDigits(mins)} دقیقه`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0
    ? `${toPersianDigits(h)} ساعت`
    : `${toPersianDigits(h)} ساعت و ${toPersianDigits(m)} دقیقه`;
};

const formatClock = () => {
  const d = new Date();
  return `${toPersianDigits(d.getHours())}:${toPersianDigits(
    String(d.getMinutes()).padStart(2, '0')
  )}`;
};

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 5) return 'شب بخیر';
  if (h < 12) return 'صبح بخیر';
  if (h < 17) return 'وقت بخیر';
  if (h < 20) return 'عصر بخیر';
  return 'شب بخیر';
};

const getGreetingEmoji = () => {
  const h = new Date().getHours();
  if (h < 5) return '🌙';
  if (h < 12) return '☀️';
  if (h < 17) return '🌤️';
  if (h < 20) return '🌆';
  return '🌙';
};

const displayGrade = (grade, field) => {
  if (!grade) return '';
  const g = getGradeMeta(normalizeGradeId(grade));
  const gTitle = g?.title || String(grade);
  if (!field) return gTitle;
  const f = getFieldMeta(normalizeFieldId(field));
  return f ? `${gTitle} ${f.shortTitle || f.title}` : gTitle;
};

const isImageUrl = (v) => {
  if (typeof v !== 'string' || !v) return false;
  if (v.length > 60) return v.startsWith('data:');
  return (
    v.startsWith('/') ||
    v.startsWith('http') ||
    v.startsWith('data:') ||
    /\.(png|jpg|jpeg|gif|webp|svg|avif)$/i.test(v)
  );
};

const downloadBlob = (filename, content, mime = 'application/json') => {
  try {
    const blob = new Blob([content], { type: `${mime};charset=utf-8` });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return true;
  } catch { return false; }
};

/* ================================================================
   🆕 ۰۱.۵ استخراج فرزند از آبجکت والد (فیلدهای -student)
   ================================================================ */
const extractChildFromParent = (parentUser) => {
  if (!parentUser || typeof parentUser !== 'object') return null;

  const childId =
    parentUser['id-student'] ||
    parentUser['studentId'] ||
    parentUser['childId'] ||
    null;

  const childPhone =
    parentUser['phone-student'] ||
    parentUser['studentPhone'] ||
    null;

  const childFirstName =
    parentUser['firstName-student'] ||
    parentUser['studentFirstName'] ||
    '';

  const childLastName =
    parentUser['lastName-student'] ||
    parentUser['studentLastName'] ||
    '';

  const childGrade =
    parentUser['grade-student'] ||
    parentUser['studentGrade'] ||
    '';

  const childField =
    parentUser['field-student'] ||
    parentUser['studentField'] ||
    null;

  const childAvatar =
    parentUser['avatar-student'] ||
    parentUser['studentAvatar'] ||
    '';

  const childCoins =
    parentUser['coins-student'] ??
    parentUser['studentCoins'] ??
    0;

  const childXp =
    parentUser['xp-student'] ??
    parentUser['studentXp'] ??
    0;

  if (!childId && !childPhone && !childFirstName) return null;

  const finalId = String(childId || childPhone || '').trim();
  if (!finalId) return null;

  const name =
    [childFirstName, childLastName].filter(Boolean).join(' ').trim() ||
    'فرزند';

  return {
    id: finalId,
    name,
    firstName: childFirstName || '',
    lastName: childLastName || '',
    grade: normalizeGradeId(childGrade) || childGrade || '',
    field: childField ? normalizeFieldId(childField) : null,
    phone: String(childPhone || childId || '').trim(),
    avatar: childAvatar || '',
    coins: Number(childCoins) || 0,
    xp: Number(childXp) || 0,
    isChild: true,
  };
};

/* ================================================================
   🆕 ۰۱.۶ غنی‌سازی داده‌های فرزند از آرایه users
   ================================================================ */
const enrichChildFromUsers = (child) => {
  if (!child) return child;

  let users = [];
  try {
    users = engine.get('users', []) || [];
  } catch { /* ignore */ }

  if (!Array.isArray(users) || users.length === 0) return child;

  const match = users.find(
    (u) =>
      u &&
      (String(u.id) === String(child.id) ||
        String(u.phone) === String(child.phone))
  );

  if (!match) return child;

  const mergedName =
    [match.firstName, match.lastName].filter(Boolean).join(' ').trim() ||
    match.name ||
    match.fullName ||
    child.name;

  return {
    ...child,
    id: String(match.id || child.id),
    name: mergedName,
    firstName: match.firstName || child.firstName,
    lastName: match.lastName || child.lastName,
    grade:
      normalizeGradeId(match.grade) ||
      match.grade ||
      child.grade ||
      '',
    field: match.field
      ? normalizeFieldId(match.field) || child.field
      : child.field,
    phone: match.phone || child.phone,
    avatar: match.avatar || child.avatar,
    coins:
      typeof match.coins === 'number' ? match.coins : child.coins,
    xp: typeof match.xp === 'number' ? match.xp : child.xp,
  };
};

/* ================================================================
   ۰۲. HOOK: مدیریت فرزندان (بدون self-view)
   ================================================================ */
const useChildren = (parentUser) => {
  const [children, setChildren] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const parentId = parentUser?.id;
  const parentRole = parentUser?.role;

  useEffect(() => {
    if (!parentId || parentRole !== 'parent') {
      setChildren([]);
      setSelectedId(null);
      return;
    }

    let list = [];

    /* ── ۱. اولویت اول: فیلدهای -student روی والد ── */
    const fromParent = extractChildFromParent(parentUser);
    if (fromParent) {
      list.push(fromParent);
    }

    /* ── ۲. اگر در parent-children-* ذخیره شده ── */
    if (list.length === 0) {
      const raw = safeRead(`parent-children-${parentId}`, []);
      if (Array.isArray(raw) && raw.length > 0) {
        list = raw
          .map((c) => {
            const cid = String(c.id || c.userId || c._id || '').trim();
            if (!cid) return null;
            return {
              id: cid,
              name:
                c.name ||
                `${c.firstName || ''} ${c.lastName || ''}`.trim() ||
                'فرزند',
              firstName: c.firstName || '',
              lastName: c.lastName || '',
              grade: normalizeGradeId(c.grade) || c.grade || '',
              field: c.field ? normalizeFieldId(c.field) : null,
              phone: c.phone || cid,
              avatar: c.avatar || '',
              coins: Number(c.coins) || 0,
              xp: Number(c.xp) || 0,
              isChild: true,
            };
          })
          .filter(Boolean);
      }
    }

    /* ── ۳. اگر parentUser.children داره ── */
    if (
      list.length === 0 &&
      Array.isArray(parentUser.children) &&
      parentUser.children.length > 0
    ) {
      list = parentUser.children
        .map((c) => {
          const cid = String(c.id || c.userId || c._id || '').trim();
          if (!cid) return null;
          return {
            id: cid,
            name:
              c.name ||
              `${c.firstName || ''} ${c.lastName || ''}`.trim() ||
              'فرزند',
            firstName: c.firstName || '',
            lastName: c.lastName || '',
            grade: normalizeGradeId(c.grade) || c.grade || '',
            field: c.field ? normalizeFieldId(c.field) : null,
            phone: c.phone || cid,
            avatar: c.avatar || '',
            coins: Number(c.coins) || 0,
            xp: Number(c.xp) || 0,
            isChild: true,
          };
        })
        .filter(Boolean);
    }

    /* ── 🚫 self-view fallback حذف شد ── */

    /* ── غنی‌سازی از users array ── */
    list = list.map(enrichChildFromUsers);

    setChildren(list);

    const persisted = safeRead(`parent-selected-child-${parentId}`, null);
    const valid = list.some((c) => String(c.id) === String(persisted));
    setSelectedId(valid ? String(persisted) : (list[0]?.id || null));
  }, [parentId, parentRole, parentUser, refreshTick]);

  useEffect(() => {
    if (!parentId) return;

    const onStorage = (e) => {
      if (!e.key) {
        setRefreshTick((t) => t + 1);
        return;
      }
      if (
        e.key === 'users' ||
        e.key === `parent-children-${parentId}` ||
        e.key.startsWith('dailyProgress_') ||
        e.key.startsWith('lesson-') ||
        e.key.startsWith('streak_') ||
        e.key.startsWith('curriculum_plans_')
      ) {
        setRefreshTick((t) => t + 1);
      }
    };

    const onFocus = () => setRefreshTick((t) => t + 1);
    const onVis = () => {
      if (!document.hidden) setRefreshTick((t) => t + 1);
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [parentId]);

  const selectChild = useCallback(
    (id) => {
      if (!parentId || !id) return;
      setSelectedId(String(id));
      try {
        engine.set(`parent-selected-child-${parentId}`, String(id));
      } catch { /* ignore */ }
    },
    [parentId]
  );

  const addChild = useCallback(
    (childData) => {
      if (!parentId || !childData?.id) return false;
      const list = safeRead(`parent-children-${parentId}`, []);
      const exists = list.some(
        (c) => String(c.id) === String(childData.id)
      );
      if (exists) return false;

      list.push({
        id: String(childData.id),
        name: childData.name || 'فرزند',
        firstName: childData.firstName || '',
        lastName: childData.lastName || '',
        grade:
          normalizeGradeId(childData.grade) || childData.grade || '',
        field: childData.field
          ? normalizeFieldId(childData.field)
          : null,
        phone: childData.phone || String(childData.id),
        avatar: childData.avatar || '',
        coins: 0,
        xp: 0,
      });
      try {
        engine.set(`parent-children-${parentId}`, list);
        setRefreshTick((t) => t + 1);
        selectChild(String(childData.id));
        return true;
      } catch {
        return false;
      }
    },
    [parentId, selectChild]
  );

  const removeChild = useCallback(
    (id) => {
      if (!parentId) return false;
      const list = safeRead(`parent-children-${parentId}`, []).filter(
        (c) => String(c.id) !== String(id)
      );
      try {
        engine.set(`parent-children-${parentId}`, list);
        setRefreshTick((t) => t + 1);
        return true;
      } catch {
        return false;
      }
    },
    [parentId]
  );

  const selected = useMemo(
    () =>
      children.find((c) => String(c.id) === String(selectedId)) ||
      children[0] ||
      null,
    [children, selectedId]
  );

  return {
    children,
    selectedId,
    selected,
    selectChild,
    addChild,
    removeChild,
  };
};

/* ================================================================
   ۰۳. HOOK: آمار کلی فرزند
   ================================================================ */
const useChildStats = (child) => {
  const { exams } = useExams();
  const [stats, setStats] = useState(() => ({
    avgPercent: 0,
    avg20: '—',
    examCount: 0,
    passed: 0,
    failed: 0,
    todayMin: 0,
    totalMin: 0,
    streak: 0,
    rank: null,
    totalFriends: 0,
    coins: 0,
    xp: 0,
    completedChallenges: 0,
    attendance: 100,
    recentScores: [],
  }));

  const childId = child?.id;
  const childGrade = child?.grade;
  const childField = child?.field;

  const compute = useCallback(() => {
    if (!childId) return;

    const childExams = (exams || []).filter((e) => {
      const r = e.results?.find(
        (x) =>
          String(x.studentId) === String(childId) && x.isFinalSubmit
      );
      if (!r) return false;
      if (
        childGrade &&
        e.grade &&
        String(e.grade) !== String(childGrade)
      )
        return false;
      if (
        childField &&
        e.field &&
        String(e.field) !== String(childField)
      )
        return false;
      return true;
    });

    let scoreSum = 0,
      maxSum = 0,
      examCount = 0,
      passed = 0;
    const recentScores = [];

    childExams.forEach((e) => {
      const r = e.results.find(
        (x) =>
          String(x.studentId) === String(childId) && x.isFinalSubmit
      );
      const maxScore =
        e.questions?.reduce(
          (s, q) => s + toNumber(q.score, 1),
          0
        ) || 0;
      if (maxScore <= 0) return;
      const score = toNumber(r.score, 0);
      const pct = (score / maxScore) * 100;
      scoreSum += score;
      maxSum += maxScore;
      examCount++;
      if (pct >= 50) passed++;
      recentScores.push({
        id: e.id,
        title: e.title || e.book || e.subject || 'آزمون',
        subject: e.subject,
        book: e.book,
        chapter: e.chapter,
        score,
        maxScore,
        pct: Math.round(pct),
        passed: pct >= 50,
        date: r.submittedAt || e.date || e.createdAt,
      });
    });

    const avgPercent =
      maxSum > 0 ? Math.round((scoreSum / maxSum) * 100) : 0;
    const avg20 =
      maxSum > 0 ? ((scoreSum / maxSum) * 20).toFixed(1) : '—';

    const daily = safeRead(`dailyProgress_${childId}`, {});
    const today = getTodayKey();
    const todayMin = Number(daily[today]) || 0;
    const totalMin = Object.values(daily).reduce(
      (s, v) => s + (Number(v) || 0),
      0
    );

    const streakData = safeRead(`streak_${childId}`, {
      streak: 0,
      lastDate: null,
    });
    const last =
      streakData.lastDate || streakData.lastActivityDate;
    const streak =
      last === today || last === getYesterdayKey()
        ? Number(streakData.streak) || 0
        : 0;

    const friends = safeRead(`friends_${childId}`, []);
    let rank = null;
    let totalFriends = Array.isArray(friends) ? friends.length : 0;
    if (Array.isArray(friends) && friends.length > 0) {
      const idx = friends.findIndex(
        (f) => f.isMe || String(f.id) === String(childId)
      );
      rank = idx >= 0 ? idx + 1 : null;
    }

    const coins = safeReadNum(`coins_${childId}`, 0);
    const xp = safeReadNum(`xp_${childId}`, 0);
    const ccRaw = engine.get(`completedChallenges_${childId}`, '0');
    const completedChallenges = parseInt(String(ccRaw ?? '0'), 10) || 0;

    let attendance = 100;
    if (childGrade) {
      const allChildGradeExams = (exams || []).filter((e) => {
        if (
          e.grade &&
          String(e.grade) !== String(childGrade)
        )
          return false;
        if (
          childField &&
          e.field &&
          String(e.field) !== String(childField)
        )
          return false;
        return (
          e.results?.some((r) => r.isFinalSubmit) || false
        );
      });
      attendance =
        allChildGradeExams.length > 0
          ? Math.round(
              (examCount / allChildGradeExams.length) * 100
            )
          : 100;
    }

    recentScores.sort(
      (a, b) => new Date(b.date || 0) - new Date(a.date || 0)
    );

    setStats({
      avgPercent,
      avg20,
      examCount,
      passed,
      failed: examCount - passed,
      todayMin,
      totalMin,
      streak,
      rank,
      totalFriends,
      coins,
      xp,
      completedChallenges,
      attendance,
      recentScores: recentScores.slice(0, 6),
    });
  }, [childId, childGrade, childField, exams]);

  useEffect(() => {
    compute();
    const watched = [
      'exams',
      `dailyProgress_${childId}`,
      `streak_${childId}`,
      `friends_${childId}`,
      `coins_${childId}`,
      `xp_${childId}`,
      `completedChallenges_${childId}`,
    ];
    const onStorage = (e) => {
      if (!e.key) return compute();
      if (
        watched.some(
          (k) => e.key === k || e.key.startsWith(k)
        )
      )
        compute();
    };
    const onFocus = () => compute();
    const onVis = () => {
      if (!document.hidden) compute();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [compute, childId]);

  return stats;
};

/* ================================================================
   ۰۴. HOOK: پیشرفت درسی
   ================================================================ */
const useChildSubjects = (child) => {
  const [subjects, setSubjects] = useState([]);
  const childId = child?.id;
  const childGrade = child?.grade;
  const childField = child?.field;

  useEffect(() => {
    if (!childId || !childGrade) {
      setSubjects([]);
      return;
    }

    const load = () => {
      const records = safeRead(`lesson-records-${childId}`, {});
      const prefix = `lesson-progress-${childId}-`;

      const progressIndex = {};
      const keys = engine.keys(prefix);   // 🆕 جای loop روی localStorage
      for (const k of keys) {
        const p = safeRead(k, {});
        progressIndex[k] = Array.isArray(p.completedSteps)
          ? p.completedSteps.length
          : 0;
      }

      const bySubject = {};
      for (const rec of Object.values(records)) {
        if (!rec) continue;
        const recGrade = normalizeGradeId(rec.grade);
        if (recGrade !== String(childGrade)) continue;
        const sub = rec.subject;
        if (!sub) continue;

        const total = Number(rec.totalSteps) || 0;
        if (total <= 0) continue;

        const pKey = `${prefix}${rec.grade}-${rec.subject}-${rec.folderName}-${rec.lessonId}`;
        const done = progressIndex[pKey] || 0;
        const ratio = Math.min(1, done / total);

        if (!bySubject[sub]) bySubject[sub] = { best: {} };
        const lessonId = rec.lessonId;
        if (
          !bySubject[sub].best[lessonId] ||
          bySubject[sub].best[lessonId] < ratio
        ) {
          bySubject[sub].best[lessonId] = ratio;
        }
      }

      const result = Object.entries(bySubject)
        .map(([subject, data]) => {
          const ratios = Object.values(data.best);
          const sum = ratios.reduce((s, r) => s + r, 0);

          let detailKey = null;
          if (childField) {
            const withField = `${childGrade}-${childField}-${subject}`;
            if (COURSE_DETAILS[withField]) detailKey = withField;
          }
          if (!detailKey) {
            const noField = `${childGrade}-${subject}`;
            if (COURSE_DETAILS[noField]) detailKey = noField;
          }
          if (!detailKey) {
            detailKey = Object.keys(COURSE_DETAILS).find((k) => {
              if (!k.startsWith(`${childGrade}-`)) return false;
              const parts = k.split('-');
              return parts[parts.length - 1] === subject;
            });
          }

          const totalLessons = detailKey
            ? Number(COURSE_DETAILS[detailKey].lessons) || 0
            : 0;

          const divisor =
            totalLessons > 0
              ? totalLessons
              : Math.max(20, ratios.length);
          const percent = Math.min(
            100,
            Math.round((sum / divisor) * 100)
          );

          const meta = getSubjectMeta(subject);
          return {
            subject,
            name: meta?.title || subject,
            percent,
            totalLessons,
            lessonsDone: ratios.filter((r) => r >= 1).length,
          };
        })
        .sort((a, b) => b.percent - a.percent);

      setSubjects(result);
    };

    load();
    const onStorage = (e) => {
      if (!e.key) return load();
      if (
        e.key === `lesson-records-${childId}` ||
        e.key.startsWith(`lesson-progress-${childId}-`)
      )
        load();
    };
    const onFocus = () => load();
    const onVis = () => {
      if (!document.hidden) load();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [childId, childGrade, childField]);

  return subjects;
};

/* ================================================================
   ۰۵. HOOK: مطالعه هفتگی
   ================================================================ */
const useChildStudy = (child) => {
  const [data, setData] = useState({
    week: [0, 0, 0, 0, 0, 0, 0],
    todayMin: 0,
    weekTotal: 0,
    maxWeek: 60,
    bookStats: [],
  });
  const childId = child?.id;

  useEffect(() => {
    if (!childId) return;

    const load = () => {
      const daily = safeRead(`dailyProgress_${childId}`, {});
      const today = getTodayKey();

      const now = new Date();
      const dayOfWeek = now.getDay();
      const saturdayOffset = (dayOfWeek + 1) % 7;
      const saturday = new Date(now);
      saturday.setDate(now.getDate() - saturdayOffset);

      const week = [];
      let total = 0;
      for (let i = 0; i < 7; i++) {
        const d = new Date(saturday);
        d.setDate(saturday.getDate() + i);
        const key = d.toISOString().split('T')[0];
        const min = Number(daily[key]) || 0;
        week.push(min);
        total += min;
      }

      const rawBooks = safeRead(`bookStats_${childId}`, {});
      const bookColors = [
        '#7c3aed',
        '#3b82f6',
        '#22c55e',
        '#f59e0b',
        '#ec4899',
        '#14b8a6',
        '#f97316',
      ];
      const bookStats = Object.entries(rawBooks)
        .filter(([, v]) => Number(v) > 0)
        .sort((a, b) => Number(b[1]) - Number(a[1]))
        .slice(0, 6)
        .map(([subject, value], i) => ({
          subject,
          name: getSubjectMeta(subject)?.title || subject,
          value: Number(value),
          color: bookColors[i % bookColors.length],
        }));

      setData({
        week,
        todayMin: Number(daily[today]) || 0,
        weekTotal: total,
        maxWeek: Math.max(...week, 60),
        bookStats,
      });
    };

    load();
    const onStorage = (e) => {
      if (
        e.key === `dailyProgress_${childId}` ||
        e.key === `weeklyProgress_${childId}` ||
        e.key === `bookStats_${childId}`
      )
        load();
    };
    const onFocus = () => load();
    const onVis = () => {
      if (!document.hidden) load();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [childId]);

  return data;
};

/* ================================================================
   ۰۶. HOOK: برنامه هفتگی
   ================================================================ */
const useChildWeeklyPlan = (child) => {
  const [data, setData] = useState({
    hasPlan: false,
    weekDays: [],
    todayTotal: 0,
    todayDone: 0,
    weekMinutes: 0,
    todaySessions: [],
  });
  const childId = child?.id;

  useEffect(() => {
    if (!childId) return;

    const load = () => {
      const plans = safeRead(`curriculum_plans_${childId}`, []);
      if (!Array.isArray(plans) || plans.length === 0) {
        setData({
          hasPlan: false,
          weekDays: [],
          todayTotal: 0,
          todayDone: 0,
          weekMinutes: 0,
          todaySessions: [],
        });
        return;
      }

      const active = plans.find((p) => p.pinned) || plans[0];
      const age =
        Date.now() -
        new Date(active.createdAt || Date.now()).getTime();
      if (age > 14 * 24 * 60 * 60 * 1000) {
        setData({
          hasPlan: false,
          weekDays: [],
          todayTotal: 0,
          todayDone: 0,
          weekMinutes: 0,
          todaySessions: [],
        });
        return;
      }

      const days = Array.isArray(active.days) ? active.days : [];
      const letters = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];
      const todayIdx = getPersianDayIndex();

      let weekMinutes = 0;
      const weekDays = letters.map((letter, idx) => {
        const sessions = Array.isArray(days[idx]?.sessions)
          ? days[idx].sessions
          : [];
        const doneCount = sessions.filter((s) => s.done).length;
        sessions.forEach((s) => {
          if (s.done) weekMinutes += Number(s.duration) || 45;
        });
        return {
          letter,
          status:
            sessions.length === 0
              ? ''
              : doneCount === sessions.length
              ? 'done'
              : 'pending',
          isToday: idx === todayIdx,
        };
      });

      const todaySessions = Array.isArray(days[todayIdx]?.sessions)
        ? days[todayIdx].sessions
        : [];

      setData({
        hasPlan: true,
        weekDays,
        todayTotal: todaySessions.length,
        todayDone: todaySessions.filter((s) => s.done).length,
        weekMinutes,
        todaySessions,
      });
    };

    load();
    const onStorage = (e) => {
      if (e.key === `curriculum_plans_${childId}`) load();
    };
    const onFocus = () => load();
    const onVis = () => {
      if (!document.hidden) load();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [childId]);

  return data;
};

/* ================================================================
   ۰۷. HOOK: مطالعه‌ی زنده
   ================================================================ */
const useChildLiveStudy = (child) => {
  const [live, setLive] = useState({
    active: false,
    book: '',
    topic: '',
    startedAt: 0,
    elapsed: 0,
  });
  const childId = child?.id;

  useEffect(() => {
    if (!childId) return;

    const load = () => {
      const raw = safeRead(`study_active_${childId}`, null);
      if (!raw || !raw.startedAt) {
        setLive({
          active: false,
          book: '',
          topic: '',
          startedAt: 0,
          elapsed: 0,
        });
        return;
      }
      const age = Date.now() - Number(raw.startedAt);
      if (age <= 0 || age > 3 * 60 * 60 * 1000) {
        setLive({
          active: false,
          book: '',
          topic: '',
          startedAt: 0,
          elapsed: 0,
        });
        return;
      }
      setLive({
        active: true,
        book: raw.book || 'مطالعه',
        topic: raw.topic || '',
        startedAt: Number(raw.startedAt),
        elapsed: Math.floor(age / 60000),
      });
    };

    load();
    const t = setInterval(load, 20000);
    const onStorage = (e) => {
      if (e.key === `study_active_${childId}`) load();
    };
    const onFocus = () => load();
    const onVis = () => {
      if (!document.hidden) load();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(t);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [childId]);

  return live;
};

/* ================================================================
   ۰۸. HOOK: پیام‌ها
   ================================================================ */
const useChildMessages = (child, parentId) => {
  const [messages, setMessages] = useState([]);
  const childId = child?.id;

  useEffect(() => {
    if (!childId) return;
    const load = () => {
      const msgs = safeRead(`parent-messages-${childId}`, []);
      setMessages(Array.isArray(msgs) ? msgs : []);
    };
    load();
    const onStorage = (e) => {
      if (e.key === `parent-messages-${childId}`) load();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [childId]);

  const sendMessage = useCallback(
    (text) => {
      if (!childId || !text || !parentId) return false;
      const msgs = safeRead(`parent-messages-${childId}`, []);
      const newMsg = {
        id: `msg_${Date.now()}_${Math.random()
          .toString(36)
          .slice(2, 6)}`,
        from: 'parent',
        parentId,
        text,
        sentAt: Date.now(),
        read: false,
      };
      try {
        engine.set(`parent-messages-${childId}`, [...msgs, newMsg]);
        setMessages([...msgs, newMsg]);
        return true;
      } catch {
        return false;
      }
    },
    [childId, parentId]
  );

  const clearMessages = useCallback(() => {
    if (!childId) return;
    try {
      engine.remove(`parent-messages-${childId}`);
      setMessages([]);
    } catch { /* ignore */ }
  }, [childId]);

  return { messages, sendMessage, clearMessages };
};

/* ================================================================
   ۰۹. HOOK: مدال‌ها
   ================================================================ */
const evaluateCondition = (def, stats) => {
  const cond =
    def?.condition || def?.requirement || def?.trigger || {};
  const type = cond.type || cond.metric || def?.type_check;
  const value = Number(
    cond.value ?? cond.target ?? def?.value ?? 0
  );

  switch (type) {
    case 'streak':
    case 'streak_days':
      return stats.streak >= value;
    case 'exam_count':
    case 'exams':
      return stats.examCount >= value;
    case 'average':
    case 'avg_score':
      return stats.avgPercent >= value;
    case 'study_minutes':
    case 'total_study':
      return stats.totalMin >= value;
    case 'challenge_count':
    case 'challenges':
      return stats.completedChallenges >= value;
    case 'coins':
      return stats.coins >= value;
    case 'xp':
      return stats.xp >= value;
    default:
      return false;
  }
};

const computeProgress = (def, stats) => {
  const cond =
    def?.condition || def?.requirement || def?.trigger || {};
  const target =
    Number(cond.value ?? cond.target ?? def?.value ?? 1) || 1;
  const type = cond.type || cond.metric;

  let current = 0;
  switch (type) {
    case 'streak':
    case 'streak_days':
      current = stats.streak;
      break;
    case 'exam_count':
    case 'exams':
      current = stats.examCount;
      break;
    case 'average':
    case 'avg_score':
      current = stats.avgPercent;
      break;
    case 'study_minutes':
    case 'total_study':
      current = stats.totalMin;
      break;
    case 'challenge_count':
    case 'challenges':
      current = stats.completedChallenges;
      break;
    case 'coins':
      current = stats.coins;
      break;
    case 'xp':
      current = stats.xp;
      break;
    default:
      current = 0;
  }
  return Math.min(100, Math.round((current / target) * 100));
};

const useChildMedals = (child, stats) => {
  return useMemo(() => {
    if (!child?.id || !stats) {
      return { unlocked: 0, total: 0, list: [], ready: false };
    }

    if (Array.isArray(ALL_MEDAL_DEFS) && ALL_MEDAL_DEFS.length > 0) {
      try {
        const allItems = ALL_MEDAL_DEFS.filter(
          (d) => d && (d.type === 'medal' || d.type === 'achievement')
        );
        if (allItems.length > 0) {
          const list = allItems.map((def) => ({
            id: def.id || def.key || Math.random().toString(36),
            name: def.name || def.title || 'دستاورد',
            icon: def.icon || '🏅',
            hint: def.description || def.hint || '',
            rare: !!def.rare,
            type: def.type,
            category: def.category || '',
            unlocked: evaluateCondition(def, stats),
            progress: computeProgress(def, stats),
          }));
          return {
            unlocked: list.filter((m) => m.unlocked).length,
            total: list.length,
            list: list.sort(
              (a, b) => (b.unlocked ? 1 : 0) - (a.unlocked ? 1 : 0)
            ),
            ready: true,
            isReal: true,
          };
        }
      } catch (e) {
        console.warn('useChildMedals: parse failed', e);
      }
    }

    const items = [
      { id: 'streak-3', name: 'شروع‌کننده', icon: '🔥', target: 3, current: stats.streak, hint: '۳ روز متوالی مطالعه' },
      { id: 'streak-7', name: 'یک هفته', icon: '💪', target: 7, current: stats.streak, hint: '۷ روز متوالی' },
      { id: 'streak-30', name: 'پایدار', icon: '🏔️', target: 30, current: stats.streak, hint: '۳۰ روز متوالی' },
      { id: 'exam-1', name: 'اولین آزمون', icon: '📝', target: 1, current: stats.examCount, hint: 'اولین آزمون' },
      { id: 'exam-10', name: 'آزمون‌دیده', icon: '🎯', target: 10, current: stats.examCount, hint: '۱۰ آزمون' },
      { id: 'avg-70', name: 'دانش‌پژوه', icon: '📚', target: 70, current: stats.avgPercent, hint: 'میانگین ۷۰٪' },
      { id: 'avg-85', name: 'نابغه', icon: '🧠', target: 85, current: stats.avgPercent, hint: 'میانگین ۸۵٪' },
      { id: 'study-60', name: 'پشتکار', icon: '⏰', target: 60, current: stats.todayMin, hint: '۱ ساعت مطالعه امروز' },
      { id: 'study-300', name: 'کوشا', icon: '🌟', target: 300, current: stats.totalMin, hint: '۵ ساعت مطالعه کل' },
      { id: 'chal-5', name: 'چالش‌گر', icon: '⚡', target: 5, current: stats.completedChallenges, hint: '۵ چالش' },
      { id: 'chal-20', name: 'قهرمان', icon: '🏆', target: 20, current: stats.completedChallenges, hint: '۲۰ چالش' },
      { id: 'coin-500', name: 'ثروتمند', icon: '💰', target: 500, current: stats.coins, hint: '۵۰۰ سکه' },
    ];
    const list = items.map((m) => ({
      ...m,
      unlocked: m.current >= m.target,
      progress: Math.min(
        100,
        Math.round((m.current / m.target) * 100)
      ),
    }));
    return {
      unlocked: list.filter((m) => m.unlocked).length,
      total: list.length,
      list: list.sort(
        (a, b) => (b.unlocked ? 1 : 0) - (a.unlocked ? 1 : 0)
      ),
      ready: true,
      isReal: false,
    };
  }, [child?.id, stats]);
};

/* ================================================================
   ۱۰. HOOK: چالش‌های فرزند
   ================================================================ */
const useChildChallenges = (child) => {
  const { exams } = useExams();
  const childId = child?.id;
  const childGrade = child?.grade;
  const childField = child?.field;

  return useMemo(() => {
    if (!childId) {
      return {
        active: [],
        completed: [],
        stats: { activeCount: 0, completedCount: 0 },
      };
    }

    const childChallenges = (exams || []).filter((e) => {
      if (e.isChallenge !== true) return false;
      if (
        childGrade &&
        e.grade &&
        String(e.grade) !== String(childGrade)
      )
        return false;
      if (
        childField &&
        e.field &&
        String(e.field) !== String(childField)
      )
        return false;
      if (e.statusType === 'archived') return false;
      return true;
    });

    const active = [];
    const completed = [];

    childChallenges.forEach((e) => {
      const r = e.results?.find(
        (x) => String(x.studentId) === String(childId)
      );
      if (r?.isFinalSubmit) {
        const maxScore =
          e.questions?.reduce(
            (s, q) => s + toNumber(q.score, 1),
            0
          ) || 0;
        const score = toNumber(r.score, 0);
        completed.push({
          id: e.id,
          title: e.title,
          score,
          maxScore,
          pct:
            maxScore > 0
              ? Math.round((score / maxScore) * 100)
              : 0,
          date: r.submittedAt,
        });
      } else {
        active.push({
          id: e.id,
          title: e.title,
          book: e.book,
          subject: e.subject,
          questions: e.questions?.length || 0,
        });
      }
    });

    completed.sort(
      (a, b) => new Date(b.date || 0) - new Date(a.date || 0)
    );

    return {
      active: active.slice(0, 4),
      completed: completed.slice(0, 4),
      stats: {
        activeCount: active.length,
        completedCount: completed.length,
      },
    };
  }, [exams, childId, childGrade, childField]);
};

/* ================================================================
   ✨ HOOK: آمار والدین روی فرزند
   ================================================================ */
const useChildParentStats = (child) => {
  const stats = useChildStats(child);
  const subjects = useChildSubjects(child);
  const study = useChildStudy(child);

  return useMemo(() => {
    const performance =
      subjects.length > 0
        ? Math.round(
            subjects.reduce(
              (s, x) => s + (x.percent || 0),
              0
            ) / subjects.length
          )
        : 0;

    const examsAvg = stats.avgPercent || 0;

    const gradeNum = normalizeGradeId(child?.grade);
    let weeklyGoal = 300;
    if (['1', '2'].includes(gradeNum)) weeklyGoal = 120;
    else if (['3', '4'].includes(gradeNum)) weeklyGoal = 180;
    else if (['5', '6'].includes(gradeNum)) weeklyGoal = 300;
    else if (['7', '8', '9'].includes(gradeNum)) weeklyGoal = 420;
    else if (gradeNum) weeklyGoal = 600;

    const weekMin = study.weekTotal || 0;
    const studyPercent = Math.min(
      100,
      Math.round((weekMin / weeklyGoal) * 100)
    );

    const base =
      performance * 0.4 +
      examsAvg * 0.35 +
      studyPercent * 0.25;
    const streakBonus = Math.min(10, stats.streak || 0);
    const total = Math.min(100, Math.round(base + streakBonus));

    return {
      performance,
      exams: examsAvg,
      study: studyPercent,
      total,
      details: {
        performance: {
          coverage: performance,
          depth: performance,
        },
        exams: { count: stats.examCount || 0 },
        study: {
          minutes: weekMin,
          goal: weeklyGoal,
          breakdown: { studyTimer: weekMin, lessonPlanning: 0 },
        },
        streak: stats.streak || 0,
      },
    };
  }, [child?.id, child?.grade, subjects, stats, study]);
};

/* ================================================================
   ۱۱. HOOK: Briefing State Machine
   ================================================================ */
const useChildBriefing = (child, parentName) => {
  const stats = useChildStats(child);
  const plan = useChildWeeklyPlan(child);
  const live = useChildLiveStudy(child);
  const { exams } = useExams();

  const [celebrationSeen, setCelebrationSeen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!child?.id) return;
    const key = `parent-briefing-seen-${child.id}-${getTodayKey()}`;
    try {
      setCelebrationSeen(engine.get(key, null) === '1');
    } catch {
      setCelebrationSeen(false);
    }
  }, [child?.id]);

  const dismissCelebration = useCallback(() => {
    if (!child?.id) return;
    try {
      engine.set(
        `parent-briefing-seen-${child.id}-${getTodayKey()}`,
        '1'
      );
    } catch { /* ignore */ }
    setCelebrationSeen(true);
  }, [child?.id]);

  const examsToday = useMemo(() => {
    if (!child?.id) return [];
    const today = getTodayKey();
    return (exams || []).filter((e) => {
      if (
        child.grade &&
        e.grade &&
        String(e.grade) !== String(child.grade)
      )
        return false;
      const r = e.results?.find(
        (x) => String(x.studentId) === String(child.id)
      );
      if (r?.isFinalSubmit) return false;
      const startAt = e.startAt || e.createdAt || e.date;
      if (!startAt) return false;
      return String(startAt).startsWith(today);
    });
  }, [exams, child]);

  const examsTomorrow = useMemo(() => {
    if (!child?.id) return [];
    const tomorrow = new Date(Date.now() + 86400000)
      .toISOString()
      .split('T')[0];
    return (exams || []).filter((e) => {
      if (
        child.grade &&
        e.grade &&
        String(e.grade) !== String(child.grade)
      )
        return false;
      const r = e.results?.find(
        (x) => String(x.studentId) === String(child.id)
      );
      if (r?.isFinalSubmit) return false;
      const startAt = e.startAt || e.createdAt || e.date;
      if (!startAt) return false;
      return String(startAt).startsWith(tomorrow);
    });
  }, [exams, child]);

  const mode = useMemo(() => {
    if (loading) return 'loading';
    if (live.active) return 'live';
    if (
      plan.hasPlan &&
      plan.todayTotal > 0 &&
      plan.todayDone === plan.todayTotal &&
      !celebrationSeen
    ) {
      return 'celebration';
    }
    const hour = new Date().getHours();
    const alerts = [];
    if (stats.todayMin === 0 && hour >= 16 && !live.active) {
      alerts.push({
        type: 'no-study',
        severity: 'warn',
        icon: 'fa-hourglass-half',
        text: 'امروز هنوز مطالعه‌ای ثبت نشده',
      });
    }
    if (
      stats.streak === 0 &&
      stats.todayMin === 0 &&
      hour >= 18
    ) {
      alerts.push({
        type: 'no-streak',
        severity: 'danger',
        icon: 'fa-fire-extinguisher',
        text: 'استریک مطالعه شکسته شده',
      });
    }
    if (stats.avgPercent < 50 && stats.examCount > 0) {
      alerts.push({
        type: 'low-avg',
        severity: 'warn',
        icon: 'fa-chart-line',
        text: `میانگین نمرات زیر ۵۰٪ (${toPersianDigits(
          stats.avgPercent
        )}٪)`,
      });
    }
    if (examsTomorrow.length > 0) {
      alerts.push({
        type: 'exam-tomorrow',
        severity: 'info',
        icon: 'fa-clipboard-list',
        text: `${toPersianDigits(
          examsTomorrow.length
        )} آزمون برای فردا`,
      });
    }
    if (
      plan.hasPlan &&
      plan.todayDone !== plan.todayTotal &&
      hour >= 20 &&
      plan.todayDone === 0
    ) {
      alerts.push({
        type: 'plan-behind',
        severity: 'danger',
        icon: 'fa-exclamation-triangle',
        text: 'برنامه‌ی امروز عقب افتاده',
      });
    }
    if (alerts.length > 0) return { kind: 'alert', alerts };
    return 'morning';
  }, [live.active, plan, celebrationSeen, stats, examsTomorrow, loading]);

  return {
    mode,
    live,
    plan,
    stats,
    examsToday,
    examsTomorrow,
    dismissCelebration,
    loading,
    parentName,
  };
};

/* ================================================================
   ۱۲. کامپوننت BriefingOrb
   ================================================================ */
const BriefingOrb = ({ avatar, name, live, size = 58 }) => {
  const isImage = isImageUrl(avatar);

  if (isImage) {
    return (
      <div
        className={`pdb-icon pdb-icon--img ${
          live ? 'pdb-icon--live' : ''
        }`}
        style={{ width: size, height: size }}
      >
        <img
          src={avatar}
          alt={name || 'avatar'}
          className="pdb-icon-img"
          onError={(e) => {
            const parent = e.currentTarget.parentElement;
            parent.classList.remove('pdb-icon--img');
            parent.classList.add('pdb-icon--emoji-fallback');
            e.currentTarget.replaceWith(
              Object.assign(document.createElement('span'), {
                className: 'pdb-icon-emoji',
                textContent: '👦',
              })
            );
          }}
        />
        {live && <span className="pdb-icon-live-dot" />}
      </div>
    );
  }

  return (
    <div
      className={`pdb-icon ${live ? 'pdb-icon--live' : ''}`}
      style={{ width: size, height: size }}
    >
      <span className="pdb-icon-emoji">{avatar || '👦'}</span>
      {live && <span className="pdb-icon-live-dot" />}
    </div>
  );
};

/* ================================================================
   ۱۳. بنر صبح
   ================================================================ */
const BriefingMorning = ({ child, briefing, onNavigate }) => {
  const { stats, plan, examsToday, examsTomorrow } = briefing;
  const greeting = getGreeting();
  const emoji = getGreetingEmoji();
  const gradeText = displayGrade(child.grade, child.field);

  return (
    <div className="pdb-card pdb-card--morning">
      <div className="pdb-glow" aria-hidden="true" />

      <div className="pdb-top">
        <span className="pdb-greet">
          <span className="pdb-greet-emoji">{emoji}</span>
          {greeting}
        </span>
        <span className="pdb-clock">{formatClock()}</span>
      </div>

      <div className="pdb-hero">
        <BriefingOrb avatar={child.avatar} name={child.name} />
        <div className="pdb-hero-text">
          <div className="pdb-title">{child.name}</div>
          <div className="pdb-sub">
            {gradeText || 'دانش‌آموز'}
            {stats.streak > 0 && (
              <>
                <span className="pdb-dot">•</span>
                <span className="pdb-streak">
                  <i className="fas fa-fire" />{' '}
                  {toPersianDigits(stats.streak)} روز
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="pdb-pills">
        <span className="pdb-pill">
          <i className="fas fa-clock" />
          {formatMinutes(stats.todayMin)} امروز
        </span>
        {plan.hasPlan && (
          <span className="pdb-pill">
            <i className="fas fa-book" />
            {toPersianDigits(plan.todayDone)}/
            {toPersianDigits(plan.todayTotal)} جلسه
          </span>
        )}
        {examsToday.length > 0 && (
          <span className="pdb-pill pdb-pill--warn">
            <i className="fas fa-pencil-alt" />
            {toPersianDigits(examsToday.length)} آزمون امروز
          </span>
        )}
        {examsTomorrow.length > 0 && (
          <span className="pdb-pill pdb-pill--info">
            <i className="fas fa-calendar-day" />
            فردا {toPersianDigits(examsTomorrow.length)} آزمون
          </span>
        )}
      </div>

      <div className="pdb-actions">
        <button
          className="pdb-btn pdb-btn--primary"
          onClick={() => onNavigate('/Lesson-planning')}
          type="button"
        >
          <i className="fas fa-calendar-alt" />
          برنامه امروز
        </button>
        {examsToday.length > 0 && (
          <button
            className="pdb-btn pdb-btn--ghost"
            onClick={() => onNavigate('/exams/student')}
            type="button"
          >
            <i className="fas fa-pencil-alt" />
            آزمون‌ها
          </button>
        )}
      </div>
    </div>
  );
};

/* ================================================================
   ۱۴. بنر Live
   ================================================================ */
const BriefingLive = ({
  child,
  briefing,
  onNavigate,
  onSendMessage,
}) => {
  const { live } = briefing;

  return (
    <div className="pdb-card pdb-card--live">
      <div className="pdb-glow" aria-hidden="true" />

      <div className="pdb-top">
        <span className="pdb-greet pdb-greet--live">
          <span className="pdb-live-dot" />
          در حال مطالعه
        </span>
        <span className="pdb-clock">{formatClock()}</span>
      </div>

      <div className="pdb-hero">
        <BriefingOrb
          avatar={child.avatar}
          name={child.name}
          live
        />
        <div className="pdb-hero-text">
          <div className="pdb-title">
            {child.name} الان در حال مطالعه است!
          </div>
          <div className="pdb-sub">
            <i className="fas fa-book-open pdb-sub-icon" />
            {live.book}
            {live.topic && (
              <>
                <span className="pdb-dot">•</span>
                {live.topic}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="pdb-pills">
        <span className="pdb-pill pdb-pill--live">
          <i className="fas fa-stopwatch" />
          {formatMinutesLong(live.elapsed)} گذشته
        </span>
        {live.startedAt > 0 && (
          <span className="pdb-pill">
            <i className="fas fa-play" />
            شروع {timeAgo(live.startedAt)}
          </span>
        )}
      </div>

      <div className="pdb-actions">
        <button
          className="pdb-btn pdb-btn--primary"
          onClick={() =>
            onSendMessage?.(
              `آفرین ${child.name} عزیز! همین‌طور ادامه بده 💪`
            )
          }
          type="button"
        >
          <i className="fas fa-heart" />
          پیام تشویقی
        </button>
        <button
          className="pdb-btn pdb-btn--ghost"
          onClick={() => onNavigate('/StudyTimer')}
          type="button"
        >
          <i className="fas fa-chart-line" />
          مشاهده لحظه‌ای
        </button>
      </div>
    </div>
  );
};

/* ================================================================
   ۱۵. بنر Alert
   ================================================================ */
const BriefingAlert = ({ child, briefing }) => {
  const alerts = briefing.mode?.alerts || [];
  const topAlerts = alerts.slice(0, 3);
  const gradeText = displayGrade(child.grade, child.field);

  return (
    <div className="pdb-card pdb-card--alert">
      <div className="pdb-glow" aria-hidden="true" />

      <div className="pdb-top">
        <span className="pdb-greet pdb-greet--alert">
          <i className="fas fa-bell" />
          نیاز به توجه
        </span>
        <span className="pdb-clock">{formatClock()}</span>
      </div>

      <div className="pdb-hero">
        <BriefingOrb avatar={child.avatar} name={child.name} />
        <div className="pdb-hero-text">
          <div className="pdb-title">{child.name}</div>
          <div className="pdb-sub">
            {gradeText || 'دانش‌آموز'} •{' '}
            {toPersianDigits(topAlerts.length)} نکته
          </div>
        </div>
      </div>

      <div className="pdb-alerts">
        {topAlerts.map((a, i) => (
          <div
            key={i}
            className={`pdb-alert-item pdb-alert-item--${a.severity}`}
          >
            <i className={`fas ${a.icon}`} />
            <span>{a.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ================================================================
   ۱۶. بنر Celebration
   ================================================================ */
const BriefingCelebration = ({
  child,
  briefing,
  onNavigate,
  onDismiss,
  onSendMessage,
}) => {
  const { stats, plan } = briefing;
  const confettiColors = [
    '#fbbf24',
    '#ec4899',
    '#8b5cf6',
    '#22c55e',
    '#3b82f6',
  ];
  const pieces = Array.from({ length: 16 }, (_, i) => i);

  return (
    <div className="pdb-card pdb-card--celebration">
      <div className="pdb-glow" aria-hidden="true" />

      <div className="pdb-confetti" aria-hidden="true">
        {pieces.map((i) => (
          <span
            key={i}
            className="pdb-confetti-piece"
            style={{
              '--i': i,
              '--x': `${(i * 7) % 100}%`,
              '--delay': `${(i % 5) * 0.18}s`,
              '--rot': `${(i * 47) % 360}deg`,
              '--color': confettiColors[i % confettiColors.length],
            }}
          />
        ))}
      </div>

      <div className="pdb-top">
        <span className="pdb-greet pdb-greet--celebrate">
          <i className="fas fa-star" />
          افتخار امروز
        </span>
        <button
          className="pdb-close"
          onClick={onDismiss}
          aria-label="بستن"
          type="button"
        >
          <i className="fas fa-check" />
        </button>
      </div>

      <div className="pdb-hero">
        <div className="pdb-icon pdb-icon--celebrate">
          <span className="pdb-icon-emoji">🎉</span>
        </div>
        <div className="pdb-hero-text">
          <div className="pdb-title pdb-title--celebrate">
            {child.name} امروز رو ترکوند!
          </div>
          <div className="pdb-sub pdb-sub--celebrate">
            <i className="fas fa-check-circle" />
            {toPersianDigits(plan.todayTotal)} جلسه انجام شد
            {stats.streak > 0 && (
              <>
                <span className="pdb-dot">•</span>
                🔥 استریک {toPersianDigits(stats.streak)} روز
              </>
            )}
          </div>
        </div>
      </div>

      <div className="pdb-actions">
        <button
          className="pdb-btn pdb-btn--primary"
          onClick={() =>
            onSendMessage?.(
              `${child.name} جان، امروز فوق‌العاده بودی! 👏🎉`
            )
          }
          type="button"
        >
          <i className="fas fa-hands-clapping" />
          آفرین بگو
        </button>
        <button
          className="pdb-btn pdb-btn--ghost"
          onClick={() => onNavigate('/medals')}
          type="button"
        >
          <i className="fas fa-medal" />
          افتخارات
        </button>
      </div>
    </div>
  );
};

/* ================================================================
   ۱۷. Skeleton
   ================================================================ */
const BriefingSkeleton = () => (
  <div className="pdb-card pdb-card--skeleton">
    <div className="pdb-skel-line pdb-skel-line--sm" />
    <div className="pdb-skel-hero">
      <div className="pdb-skel-orb" />
      <div className="pdb-skel-text">
        <div className="pdb-skel-line pdb-skel-line--lg" />
        <div className="pdb-skel-line pdb-skel-line--md" />
      </div>
    </div>
  </div>
);

/* ================================================================
   ۱۸. ParentDailyBriefing — Dispatcher
   ================================================================ */
const ParentDailyBriefing = ({
  child,
  parentName,
  onNavigate,
  onSendMessage,
  onSendReminder,
}) => {
  const briefing = useChildBriefing(child, parentName);

  if (!child) return null;

  const mode = briefing.mode;

  if (mode === 'loading') return <BriefingSkeleton />;

  if (mode === 'live') {
    return (
      <BriefingLive
        child={child}
        briefing={briefing}
        onNavigate={onNavigate}
        onSendMessage={onSendMessage}
      />
    );
  }
  if (mode === 'celebration') {
    return (
      <BriefingCelebration
        child={child}
        briefing={briefing}
        onNavigate={onNavigate}
        onDismiss={briefing.dismissCelebration}
        onSendMessage={onSendMessage}
      />
    );
  }
  if (typeof mode === 'object' && mode.kind === 'alert') {
    return (
      <BriefingAlert
        child={child}
        briefing={briefing}
        onSendReminder={onSendReminder}
      />
    );
  }
  return (
    <BriefingMorning
      child={child}
      briefing={briefing}
      onNavigate={onNavigate}
    />
  );
};

/* ================================================================
   ۱۹. UI پایه
   ================================================================ */
const Card = ({
  title,
  badge,
  badgeColor,
  icon,
  iconColor,
  children,
  footer,
}) => (
  <div className="card-modern">
    <div className="card-header">
      <div className="card-header-left">
        <i
          className={`fas ${icon}`}
          style={iconColor ? { color: iconColor } : {}}
        />
        <span>{title}</span>
      </div>
      {badge && (
        <span className={`badge badge-${badgeColor}`}>{badge}</span>
      )}
    </div>
    <div className="card-body">{children}</div>
    {footer}
  </div>
);

const StatWidget = ({
  icon,
  value,
  label,
  color,
  trend,
  trendValue,
}) => (
  <div className="stat-widget">
    <div className={`stat-icon ${color}`}>
      <i className={`fas ${icon}`}></i>
    </div>
    <div className="stat-info">
      <div className="stat-number">{value}</div>
      <div className="stat-label">{label}</div>
      {trend && (
        <div className={`stat-trend ${trend}`}>
          <i
            className={`fas fa-${
              trend === 'up' ? 'arrow-up' : 'arrow-down'
            }`}
          />
          {trendValue}
        </div>
      )}
    </div>
  </div>
);

const RingItem = ({ label, percent }) => {
  const clamped = Math.min(100, Math.max(0, percent));
  const hue = clamped >= 75 ? 142 : clamped >= 50 ? 38 : 0;
  return (
    <div className="ring-item">
      <div
        className="ring-progress"
        style={{
          background: `conic-gradient(
            hsl(${hue}, 75%, 55%) 0% ${clamped}%,
            rgba(${
              hue === 142
                ? '34,197,94'
                : hue === 38
                ? '245,158,11'
                : '239,68,68'
            }, 0.12) ${clamped}% 100%
          )`,
        }}
      >
        <span>{toPersianDigits(clamped)}%</span>
      </div>
      <span className="ring-label">{label}</span>
    </div>
  );
};

const EmptyState = ({ icon = '📭', title, hint }) => (
  <div className="parent-empty">
    <div>{icon}</div>
    <div>{title}</div>
    {hint && <div>{hint}</div>}
  </div>
);

/* ================================================================
   ۲۰. کارت مطالعه هفتگی
   ================================================================ */
const WeeklyStudyBars = ({ week, maxWeek }) => {
  const letters = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];
  const todayIdx = getPersianDayIndex();

  return (
    <div className="parent-week-bars">
      {week.map((min, i) => {
        const height =
          min > 0
            ? Math.max(8, Math.round((min / maxWeek) * 100))
            : 4;
        const isToday = i === todayIdx;
        return (
          <div
            key={i}
            className={`parent-week-bar ${
              isToday ? 'today' : ''
            }`}
          >
            <div
              className={`parent-week-bar-fill ${
                min === 0 ? 'empty' : ''
              }`}
              style={{ height: `${height}%` }}
              title={`${toPersianDigits(min)} دقیقه`}
            />
            <span className="parent-week-bar-label">
              {letters[i]}
            </span>
          </div>
        );
      })}
    </div>
  );
};

/* ================================================================
   👶 ChildDashboardCard — پیش‌نمایش داشبورد فرزند
   ================================================================ */
const ChildDashboardCard = ({ child, navigate }) => {
  const {
    performance,
    exams: examAvg,
    study,
    total,
    details,
  } = useChildParentStats(child);

  const perfTip = `عملکرد: میانگین پیشرفت ${details.performance.coverage}٪ در ${details.exams.count || 0} درس`;

  const examTip =
    details.exams.count > 0
      ? `میانگین ${details.exams.count} آزمون ثبت‌شده`
      : 'هنوز آزمون تصحیح‌شده‌ای ندارد';

  const studyTip = `این هفته: ${details.study.minutes} دقیقه از هدف ${details.study.goal} دقیقه`;

  const totalTip = `۴۰٪ عملکرد + ۳۵٪ آزمون + ۲۵٪ مطالعه + پاداش استریک (${details.streak} روز)`;

  const gradeText = displayGrade(child?.grade, child?.field);

  const handleOpen = () => {
    if (!child?.id) return;
    const url = `/parent-dashboard?childId=${encodeURIComponent(
      child.id
    )}`;
    navigate(url);
  };

  return (
    <div className="parent-card">
      <div className="parent-head">
        <div className="parent-avatar">
          <i className="fas fa-user-graduate" />
        </div>
        <div className="parent-head-text">
          <span className="parent-title">
            {child?.name || 'فرزند'} — داشبورد کامل
          </span>
          <span className="parent-sub">
            {gradeText || 'گزارش کامل در دسترس'}
          </span>
        </div>
      </div>

      <div className="parent-rings">
        <div className="parent-rings-visual">
          <svg
            className="parent-rings-svg"
            viewBox="0 0 130 130"
          >
            <defs>
              <linearGradient
                id="phChildGrad1"
                gradientUnits="userSpaceOnUse"
                x1="10"
                y1="10"
                x2="120"
                y2="120"
              >
                <stop offset="0%" stopColor="#a78bfa" />
                <stop offset="100%" stopColor="#7c3aed" />
              </linearGradient>
              <linearGradient
                id="phChildGrad2"
                gradientUnits="userSpaceOnUse"
                x1="20"
                y1="20"
                x2="110"
                y2="110"
              >
                <stop offset="0%" stopColor="#f472b6" />
                <stop offset="100%" stopColor="#ec4899" />
              </linearGradient>
              <linearGradient
                id="phChildGrad3"
                gradientUnits="userSpaceOnUse"
                x1="35"
                y1="35"
                x2="95"
                y2="95"
              >
                <stop offset="0%" stopColor="#5eead4" />
                <stop offset="100%" stopColor="#18b9ab" />
              </linearGradient>
            </defs>

            <g className="parent-ring-1">
              <circle
                className="parent-ring-track"
                cx="65"
                cy="65"
                r="58"
                fill="none"
              />
              <circle
                className="parent-ring-progress"
                cx="65"
                cy="65"
                r="58"
                fill="none"
                stroke="url(#phChildGrad1)"
                strokeDasharray={2 * Math.PI * 58}
                strokeDashoffset={
                  2 *
                  Math.PI *
                  58 *
                  (1 -
                    Math.min(
                      100,
                      Math.max(0, performance)
                    ) /
                      100)
                }
              />
            </g>

            <g className="parent-ring-2">
              <circle
                className="parent-ring-track"
                cx="65"
                cy="65"
                r="43"
                fill="none"
              />
              <circle
                className="parent-ring-progress"
                cx="65"
                cy="65"
                r="43"
                fill="none"
                stroke="url(#phChildGrad2)"
                strokeDasharray={2 * Math.PI * 43}
                strokeDashoffset={
                  2 *
                  Math.PI *
                  43 *
                  (1 - Math.min(100, Math.max(0, examAvg)) / 100)
                }
              />
            </g>

            <g className="parent-ring-3">
              <circle
                className="parent-ring-track"
                cx="65"
                cy="65"
                r="28"
                fill="none"
              />
              <circle
                className="parent-ring-progress"
                cx="65"
                cy="65"
                r="28"
                fill="none"
                stroke="url(#phChildGrad3)"
                strokeDasharray={2 * Math.PI * 28}
                strokeDashoffset={
                  2 *
                  Math.PI *
                  28 *
                  (1 - Math.min(100, Math.max(0, study)) / 100)
                }
              />
            </g>
          </svg>

          <div
            className="parent-rings-center"
            title={`میانگین سه معیار:\nعملکرد ${performance}٪ • آزمون ${examAvg}٪ • مطالعه ${study}٪`}
          >
            <span className="parent-rings-value">
              {toPersianDigits(total)}٪
            </span>
            <span className="parent-rings-caption">امتیاز کل</span>
          </div>
        </div>

        <div className="parent-legend">
          <div className="parent-legend-item" title={perfTip}>
            <span className="parent-legend-dot d1" />
            <div className="parent-legend-text">
              <span className="parent-legend-title">عملکرد</span>
              <span className="parent-legend-value">
                {toPersianDigits(performance)}٪
              </span>
            </div>
          </div>

          <div className="parent-legend-item" title={examTip}>
            <span className="parent-legend-dot d2" />
            <div className="parent-legend-text">
              <span className="parent-legend-title">آزمون‌ها</span>
              <span className="parent-legend-value">
                {toPersianDigits(examAvg)}٪
              </span>
            </div>
          </div>

          <div className="parent-legend-item" title={studyTip}>
            <span className="parent-legend-dot d3" />
            <div className="parent-legend-text">
              <span className="parent-legend-title">مطالعه</span>
              <span className="parent-legend-value">
                {toPersianDigits(study)}٪
              </span>
            </div>
          </div>
        </div>
      </div>

      <button
        className="parent-cta"
        onClick={handleOpen}
        type="button"
        title={totalTip}
      >
        <i className="fas fa-arrow-left" />
        مشاهده داشبورد کامل {child?.name || ''}
      </button>
    </div>
  );
};

/* ================================================================
   ۲۱. مودال افزودن فرزند
   ================================================================ */
const AddChildModal = ({ isOpen, onClose, onAdd }) => {
  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [grade, setGrade] = useState('');

  useEffect(() => {
    if (isOpen) {
      setId('');
      setName('');
      setGrade('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (!id.trim()) return;
    onAdd({
      id: id.trim(),
      name: name.trim() || 'فرزند',
      grade: normalizeGradeId(grade.trim()) || grade.trim() || '',
    });
    onClose();
  };

  return (
    <div
      className="ph-modal-backdrop"
      onClick={(e) =>
        e.target === e.currentTarget && onClose()
      }
    >
      <div className="ph-modal">
        <div className="ph-modal-header">
          <span className="ph-modal-title">افزودن فرزند</span>
          <button
            className="ph-modal-close"
            onClick={onClose}
            type="button"
          >
            <i className="fas fa-times" />
          </button>
        </div>
        <div className="ph-modal-body">
          <div className="ph-field">
            <label>شناسه (ID) فرزند</label>
            <input
              type="text"
              value={id}
              onChange={(e) => setId(e.target.value)}
              placeholder="مثال: 09123456789"
              dir="ltr"
            />
          </div>
          <div className="ph-field">
            <label>نام فرزند</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="علی رضایی"
            />
          </div>
          <div className="ph-field">
            <label>پایه تحصیلی (۱ تا ۱۲)</label>
            <input
              type="text"
              value={grade}
              onChange={(e) => setGrade(e.target.value)}
              placeholder="دهم / 10"
            />
          </div>
        </div>
        <div className="ph-modal-footer">
          <button
            className="ph-btn ph-btn-ghost"
            onClick={onClose}
            type="button"
          >
            انصراف
          </button>
          <button
            className="ph-btn ph-btn-primary"
            onClick={handleSubmit}
            type="button"
            disabled={!id.trim()}
          >
            <i className="fas fa-plus" /> افزودن
          </button>
        </div>
      </div>
    </div>
  );
};

/* ================================================================
   ۲۲. ParentDashboard (صفحه اصلی والد)
   ================================================================ */
const ParentDashboard = ({ onThemeChange, isDark }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const parentName =
    `${user?.firstName || ''} ${user?.lastName || ''}`.trim() ||
    user?.username ||
    'والد';

  const {
    children,
    selectedId,
    selected,
    selectChild,
    addChild,
    removeChild,
  } = useChildren(user);

  const [toast, setToast] = useState(null);
  const [addChildOpen, setAddChildOpen] = useState(false);

  const stats = useChildStats(selected);
  const subjects = useChildSubjects(selected);
  const study = useChildStudy(selected);
  const plan = useChildWeeklyPlan(selected);
  const { messages, sendMessage, clearMessages } =
    useChildMessages(selected, user?.id);
  const medals = useChildMedals(selected, stats);
  const challenges = useChildChallenges(selected);

  const showToast = useCallback((text, type = 'success') => {
    setToast({ text, type, id: Date.now() });
    setTimeout(() => setToast(null), 2600);
  }, []);

  const handleSendMessage = useCallback(
    (message) => {
      if (!selected?.id || !message) return;
      const ok = sendMessage(message);
      if (ok) showToast('پیام برای فرزندتان ارسال شد ✓');
      else showToast('ارسال پیام ناموفق بود', 'error');
    },
    [selected, sendMessage, showToast]
  );

  const handleSendReminder = useCallback(() => {
    if (!selected?.id) return;
    handleSendMessage(
      'یادآور: فراموش نکن برنامه‌ی امروزت رو کامل کنی! ⏰'
    );
  }, [selected, handleSendMessage]);

  const handleDownloadMonthly = useCallback(() => {
    if (!selected?.id) return;
    const daily = safeRead(`dailyProgress_${selected.id}`, {});
    const report = {
      child: {
        id: selected.id,
        name: selected.name,
        grade: selected.grade,
        field: selected.field,
      },
      generatedAt: new Date().toISOString(),
      stats: {
        totalStudyMinutes: stats.totalMin,
        avgScore: stats.avg20,
        avgPercent: stats.avgPercent,
        examCount: stats.examCount,
        passed: stats.passed,
        failed: stats.failed,
        streak: stats.streak,
        coins: stats.coins,
        xp: stats.xp,
        rank: stats.rank,
        totalFriends: stats.totalFriends,
      },
      subjects: subjects.map((s) => ({
        subject: s.name,
        percent: s.percent,
      })),
      dailyStudy: daily,
    };
    const ok = downloadBlob(
      `parent-report-${selected.name}-${getTodayKey()}.json`,
      JSON.stringify(report, null, 2)
    );
    showToast(
      ok ? 'گزارش دانلود شد ✓' : 'دانلود ناموفق',
      ok ? 'success' : 'error'
    );
  }, [selected, stats, subjects, showToast]);

  const handleDownloadExams = useCallback(() => {
    if (!selected?.id) return;
    const rows = [
      ['تاریخ', 'عنوان', 'درس', 'کتاب', 'نمره', 'از', 'درصد', 'وضعیت'],
      ...stats.recentScores.map((s) => [
        s.date || '',
        s.title || '',
        s.subject || '',
        s.book || '',
        s.score,
        s.maxScore,
        `${s.pct}%`,
        s.passed ? 'قبول' : 'مردود',
      ]),
    ];
    const csv =
      '\uFEFF' +
      rows
        .map((r) => r.map((v) => `"${v}"`).join(','))
        .join('\n');
    const ok = downloadBlob(
      `exams-${selected.name}-${getTodayKey()}.csv`,
      csv,
      'text/csv'
    );
    showToast(
      ok ? 'گزارش آزمون‌ها دانلود شد ✓' : 'دانلود ناموفق',
      ok ? 'success' : 'error'
    );
  }, [selected, stats, showToast]);

  const handleDownloadStudy = useCallback(() => {
    if (!selected?.id) return;
    const daily = safeRead(`dailyProgress_${selected.id}`, {});
    const rows = [['تاریخ', 'دقیقه']];
    Object.entries(daily)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .forEach(([d, m]) => rows.push([d, m]));
    const csv =
      '\uFEFF' + rows.map((r) => r.join(',')).join('\n');
    const ok = downloadBlob(
      `study-${selected.name}-${getTodayKey()}.csv`,
      csv,
      'text/csv'
    );
    showToast(
      ok ? 'گزارش مطالعه دانلود شد ✓' : 'دانلود ناموفق',
      ok ? 'success' : 'error'
    );
  }, [selected, showToast]);

  const handleRemoveChild = useCallback(() => {
    if (!selected) return;
    if (
      !window.confirm(`حذف ${selected.name} از لیست فرزندان؟`)
    )
      return;
    removeChild(selected.id);
    showToast('فرزند حذف شد');
  }, [selected, removeChild, showToast]);

  const hasAnyData =
    stats.examCount > 0 ||
    stats.totalMin > 0 ||
    subjects.length > 0 ||
    plan.hasPlan ||
    messages.length > 0 ||
    challenges.stats.completedCount > 0 ||
    stats.coins > 0 ||
    stats.xp > 0;

  return (
    <div className="role-section parent-section active">
      {/* ===== هدر خوش‌آمد والد ===== */}
      <div className="parent-welcome">
        <div className="pw-left">
          <div className="pw-avatar">
            {isImageUrl(user?.avatar) ? (
              <img src={user.avatar} alt="parent" />
            ) : (
              <span>
                {user?.avatar || (parentName || 'و').charAt(0)}
              </span>
            )}
          </div>
          <div className="pw-text">
            <div className="pw-greet">سلام {parentName} 👋</div>
            <div className="pw-sub">
              {children.length > 0
                ? `در حال مشاهده‌ی ${children.length === 1 ? 'فرزند' : `${toPersianDigits(children.length)} فرزند`}`
                : 'پنل والدین'}
            </div>
          </div>
        </div>
        <button
          className="pw-theme-btn"
          onClick={onThemeChange}
          title={isDark ? 'تم روشن' : 'تم تاریک'}
          type="button"
        >
          <i className={`fas ${isDark ? 'fa-sun' : 'fa-moon'}`} />
        </button>
      </div>

      {/* ===== انتخاب فرزند (اگر بیش از یک فرزند) ===== */}
      {children.length > 1 && (
        <div className="parent-header">
          <div className="child-selector">
            <i className="fas fa-user-circle" />
            <select
              value={selectedId || ''}
              onChange={(e) => selectChild(e.target.value)}
            >
              {children.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {c.grade
                    ? ` – ${displayGrade(c.grade, c.field)}`
                    : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {!selected ? (
        <div className="parent-no-child-state">
          <EmptyState
            icon="👶"
            title="هیچ فرزندی به حساب شما متصل نیست"
            hint="اطلاعات فرزند شما هنگام ثبت‌نام وارد نشده است. لطفاً با پشتیبانی تماس بگیرید یا از حساب خود خارج شده و مجدداً ثبت‌نام کنید و این بار اطلاعات دانش‌آموز را کامل وارد نمایید."
          />
          <div
            style={{
              display: 'flex',
              gap: 8,
              marginTop: 12,
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <button
              className="btn-outline btn-sm"
              onClick={() => setAddChildOpen(true)}
              type="button"
            >
              <i className="fas fa-plus" /> افزودن فرزند
            </button>
            <button
              className="btn-outline btn-sm"
              onClick={() => navigate('/login')}
              type="button"
            >
              <i className="fas fa-sign-in-alt" /> ورود مجدد
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* ===== بنر هوشمند ===== */}
          <ParentDailyBriefing
            child={selected}
            parentName={parentName}
            onNavigate={navigate}
            onSendMessage={handleSendMessage}
            onSendReminder={handleSendReminder}
          />

          {/* ===== آمار ===== */}
          <div className="stats-scroll">
            <div className="stats-scroll-inner">
              <StatWidget
                icon="fa-star"
                value={stats.avg20}
                label="معدل"
                color="primary"
              />
              <StatWidget
                icon="fa-check-circle"
                value={`${toPersianDigits(stats.attendance)}٪`}
                label="حضور"
                color="success"
              />
              <StatWidget
                icon="fa-pencil-alt"
                value={toPersianDigits(stats.examCount)}
                label="آزمون"
                color="warning"
              />
              <StatWidget
                icon="fa-clock"
                value={formatMinutes(stats.totalMin)}
                label="مطالعه"
                color="purple"
              />
              <StatWidget
                icon="fa-trophy"
                value={
                  stats.rank
                    ? `#${toPersianDigits(stats.rank)}`
                    : '—'
                }
                label="رتبه"
                color="info"
              />
              <StatWidget
                icon="fa-coins"
                value={toPersianDigits(stats.coins)}
                label="سکه"
                color="gold"
              />
            </div>
          </div>

          {/* ===== ردیف ۱: پیشرفت + نمرات ===== */}
          <div className="row-grid">
            <Card
              title="پیشرفت درسی"
              badge={`${toPersianDigits(
                subjects.length
              )} درس`}
              badgeColor="primary"
              icon="fa-chart-line"
            >
              {subjects.length === 0 ? (
                <EmptyState
                  icon="📚"
                  title="هنوز درسی شروع نشده"
                  hint="وقتی فرزندتان شروع کند، اینجا نمایش داده می‌شود"
                />
              ) : (
                <>
                  <div className="progress-rings">
                    {subjects.slice(0, 4).map((s) => (
                      <RingItem
                        key={s.subject}
                        label={s.name}
                        percent={s.percent}
                      />
                    ))}
                  </div>
                  {subjects[0] && (
                    <div className="ai-insight">
                      <i className="fas fa-robot" /> {selected.name}{' '}
                      در {subjects[0].name} بهترین عملکرد را دارد
                    </div>
                  )}
                </>
              )}
            </Card>

            <Card
              title="نمرات اخیر"
              badge={`${toPersianDigits(
                stats.recentScores.length
              )} آزمون`}
              badgeColor="warning"
              icon="fa-table"
            >
              {stats.recentScores.length === 0 ? (
                <EmptyState
                  icon="📝"
                  title="هنوز آزمونی ثبت نشده"
                  hint="پس از شرکت در آزمون، نمرات اینجا نمایش داده می‌شود"
                />
              ) : (
                <>
                  {stats.recentScores.slice(0, 5).map((s) => {
                    const color =
                      s.pct >= 75
                        ? 'success'
                        : s.pct >= 50
                        ? 'warning'
                        : 'danger';
                    return (
                      <div key={s.id} className="score-item">
                        <span
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: 130,
                          }}
                        >
                          {s.subject || s.title}
                        </span>
                        <span
                          className={`score-value text-${color}`}
                        >
                          {toPersianDigits(s.score)} /{' '}
                          {toPersianDigits(s.maxScore)}
                        </span>
                      </div>
                    );
                  })}
                  {/* ✅ FIXED: حالا childId در URL ارسال می‌شود */}
                  <button
                    className="btn-outline btn-sm w-full"
                    onClick={() =>
                      navigate(
                        `/parent-dashboard?childId=${encodeURIComponent(
                          selected.id
                        )}`
                      )
                    }
                    type="button"
                  >
                    گزارش کامل
                  </button>
                </>
              )}
            </Card>
          </div>

          {/* ===== ردیف ۲: مطالعه + برنامه ===== */}
          <div className="row-grid">
            <Card
              title="مطالعه هفتگی"
              badge={formatMinutes(study.weekTotal)}
              badgeColor="purple"
              icon="fa-clock"
            >
              <WeeklyStudyBars
                week={study.week}
                maxWeek={study.maxWeek}
              />
              <div className="rank-info" style={{ marginTop: 8 }}>
                <span>
                  <i className="fas fa-sun" /> امروز:{' '}
                  {formatMinutesLong(study.todayMin)}
                </span>
                <span>
                  <i className="fas fa-calendar-week" /> هفته:{' '}
                  {formatMinutesLong(study.weekTotal)}
                </span>
              </div>
            </Card>

            <Card
              title="برنامه‌ی درسی"
              badge={
                plan.hasPlan
                  ? `${toPersianDigits(
                      plan.todayDone
                    )}/${toPersianDigits(plan.todayTotal)}`
                  : '—'
              }
              badgeColor={
                plan.hasPlan &&
                plan.todayDone === plan.todayTotal
                  ? 'success'
                  : 'primary'
              }
              icon="fa-calendar-alt"
            >
              {!plan.hasPlan ? (
                <EmptyState
                  icon="📅"
                  title="برنامه‌ای تنظیم نشده"
                  hint="فرزندتان هنوز برنامه‌ی هفتگی نساخته"
                />
              ) : (
                <>
                  <div className="planner-week">
                    {plan.weekDays.map((d, i) => (
                      <div
                        key={i}
                        className={`planner-day ${
                          d.isToday ? 'today' : ''
                        }`}
                      >
                        <span className="planner-day-letter">
                          {d.letter}
                        </span>
                        <span
                          className={`planner-day-dot ${
                            d.status === 'done'
                              ? 'done'
                              : d.status === 'pending'
                              ? 'pending'
                              : ''
                          }`}
                        />
                      </div>
                    ))}
                  </div>
                  {plan.todaySessions.length > 0 && (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 6,
                      }}
                    >
                      {plan.todaySessions
                        .slice(0, 3)
                        .map((s, i) => (
                          <div
                            key={i}
                            className="score-item"
                            style={{ minHeight: 34 }}
                          >
                            <span style={{ fontSize: '0.7rem' }}>
                              {s.time} — {s.book || 'جلسه'}
                            </span>
                            <span
                              style={{
                                fontSize: '0.62rem',
                                fontWeight: 800,
                                color: s.done
                                  ? '#16a34a'
                                  : '#d97706',
                              }}
                            >
                              {s.done
                                ? '✓ انجام شد'
                                : 'در انتظار'}
                            </span>
                          </div>
                        ))}
                    </div>
                  )}
                  <button
                    className="btn-outline btn-sm w-full"
                    onClick={() => navigate('/Lesson-planning')}
                    type="button"
                    style={{ marginTop: 8 }}
                  >
                    مشاهده برنامه کامل
                  </button>
                </>
              )}
            </Card>
          </div>

          {/* ===== ردیف ۳: افتخارات + چالش‌ها ===== */}
          <div className="row-grid">
            <Card
              title="افتخارات"
              badge={`${toPersianDigits(
                medals.unlocked
              )}/${toPersianDigits(medals.total)}`}
              badgeColor="warning"
              icon="fa-medal"
            >
              {medals.list.length === 0 ? (
                <EmptyState
                  icon="🏅"
                  title="هنوز افتخاری کسب نشده"
                />
              ) : (
                <>
                  <div
                    className="medals-quick-stats"
                    style={{ marginBottom: 10 }}
                  >
                    <div className="medals-quick-item">
                      <i
                        className="fas fa-medal"
                        style={{ color: '#fbbf24' }}
                      />
                      <span className="num">
                        {toPersianDigits(medals.unlocked)}
                      </span>
                      <span>باز شده</span>
                    </div>
                    <div className="medals-quick-item">
                      <i
                        className="fas fa-fire"
                        style={{ color: '#f97316' }}
                      />
                      <span className="num">
                        {toPersianDigits(stats.streak)}
                      </span>
                      <span>استریک</span>
                    </div>
                    <div className="medals-quick-item">
                      <i
                        className="fas fa-bolt"
                        style={{ color: '#8b5cf6' }}
                      />
                      <span className="num">
                        {toPersianDigits(stats.xp)}
                      </span>
                      <span>XP</span>
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(4, 1fr)',
                      gap: 6,
                      marginBottom: 8,
                    }}
                  >
                    {medals.list.slice(0, 4).map((m) => (
                      <div
                        key={m.id}
                        title={`${m.name} — ${m.hint}${
                          !m.unlocked
                            ? ` (${m.progress}٪)`
                            : ''
                        }`}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 2,
                          padding: '6px 2px',
                          borderRadius: 10,
                          background: m.unlocked
                            ? 'linear-gradient(135deg, rgba(251,191,36,0.15), rgba(245,158,11,0.08))'
                            : 'var(--ph-bg-muted)',
                          border: `1px solid ${
                            m.unlocked
                              ? 'rgba(251,191,36,0.4)'
                              : 'rgba(148,163,184,0.15)'
                          }`,
                          opacity: m.unlocked ? 1 : 0.55,
                          filter: m.unlocked
                            ? 'none'
                            : 'grayscale(0.7)',
                        }}
                      >
                        <span style={{ fontSize: '1rem' }}>
                          {m.icon}
                        </span>
                        <span
                          style={{
                            fontSize: '0.52rem',
                            fontWeight: 700,
                            color: 'var(--ph-text-secondary)',
                            textAlign: 'center',
                          }}
                        >
                          {m.name}
                        </span>
                      </div>
                    ))}
                  </div>
                  <button
                    className="btn-outline btn-sm w-full"
                    onClick={() => navigate('/medals')}
                    type="button"
                  >
                    مشاهده همه افتخارات
                  </button>
                </>
              )}
            </Card>

            <Card
              title="چالش‌ها"
              badge={`${toPersianDigits(
                challenges.stats.activeCount
              )} فعال`}
              badgeColor="danger"
              icon="fa-fire"
            >
              {challenges.stats.activeCount === 0 &&
              challenges.stats.completedCount === 0 ? (
                <EmptyState
                  icon="⚡"
                  title="چالشی موجود نیست"
                  hint="به‌زودی چالش‌های جدید اضافه می‌شود"
                />
              ) : (
                <>
                  {challenges.active.length > 0 && (
                    <>
                      <div
                        style={{
                          fontSize: '0.62rem',
                          fontWeight: 800,
                          color: '#dc2626',
                          marginBottom: 4,
                        }}
                      >
                        فعال
                      </div>
                      {challenges.active.slice(0, 3).map((c) => (
                        <div
                          key={c.id}
                          className="score-item"
                          style={{ minHeight: 34 }}
                        >
                          <span
                            style={{
                              fontSize: '0.7rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: 130,
                            }}
                          >
                            {c.title}
                          </span>
                          <span
                            style={{
                              fontSize: '0.62rem',
                              color: '#f59e0b',
                              fontWeight: 800,
                            }}
                          >
                            {toPersianDigits(c.questions)} سوال
                          </span>
                        </div>
                      ))}
                    </>
                  )}
                  {challenges.completed.length > 0 && (
                    <>
                      <div
                        style={{
                          fontSize: '0.62rem',
                          fontWeight: 800,
                          color: '#16a34a',
                          marginTop: 8,
                          marginBottom: 4,
                        }}
                      >
                        تکمیل‌شده
                      </div>
                      {challenges.completed
                        .slice(0, 2)
                        .map((c) => (
                          <div
                            key={c.id}
                            className="score-item"
                            style={{ minHeight: 34 }}
                          >
                            <span
                              style={{
                                fontSize: '0.7rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: 130,
                              }}
                            >
                              {c.title}
                            </span>
                            <span
                              className={`score-value text-${
                                c.pct >= 75
                                  ? 'success'
                                  : c.pct >= 50
                                  ? 'warning'
                                  : 'danger'
                              }`}
                            >
                              {toPersianDigits(c.pct)}%
                            </span>
                          </div>
                        ))}
                    </>
                  )}
                  <button
                    className="btn-outline btn-sm w-full"
                    onClick={() => navigate('/Challenge')}
                    type="button"
                    style={{ marginTop: 8 }}
                  >
                    مشاهده چالش‌ها
                  </button>
                </>
              )}
            </Card>
          </div>

          {/* ===== ردیف ۴: ارتباط + گزارش‌ها ===== 
          <div className="row-grid">
            <Card
              title="ارتباط با فرزند"
              badge={`${toPersianDigits(
                messages.length
              )} پیام`}
              badgeColor="primary"
              icon="fa-comment-dots"
            >
              {messages.length === 0 ? (
                <EmptyState
                  icon="💬"
                  title="هنوز پیامی رد و بدل نشده"
                  hint="اولین پیام را بفرستید"
                />
              ) : (
                <>
                  {messages
                    .slice(-2)
                    .reverse()
                    .map((m) => (
                      <div key={m.id} className="parent-msg">
                        <div className="msg-avatar primary">
                          {m.from === 'parent'
                            ? 'ش'
                            : selected.name.charAt(0)}
                        </div>
                        <div className="msg-content">
                          <div className="msg-name">
                            {m.from === 'parent'
                              ? 'شما'
                              : selected.name}
                          </div>
                          <div className="msg-text">{m.text}</div>
                        </div>
                        <span className="badge bg-secondary">
                          {timeAgo(m.sentAt)}
                        </span>
                      </div>
                    ))}
                </>
              )}
              <div
                className="parent-actions"
                style={{
                  display: 'flex',
                  gap: 6,
                  marginTop: 8,
                }}
              >
                <button
                  className="btn-primary btn-sm"
                  onClick={() => {
                    const text = window.prompt('پیام شما:');
                    if (text?.trim())
                      handleSendMessage(text.trim());
                  }}
                  type="button"
                >
                  <i className="fas fa-comment" /> ارسال پیام
                </button>
                <button
                  className="btn-outline btn-sm"
                  onClick={clearMessages}
                  type="button"
                >
                  <i className="fas fa-eraser" /> پاک‌سازی
                </button>
              </div>
            </Card>

            <Card
              title="گزارش‌ها"
              badge="JSON/CSV"
              badgeColor="info"
              icon="fa-file-alt"
            >
              <div
                className="report-download"
                onClick={handleDownloadMonthly}
                role="button"
                tabIndex={0}
                style={{ cursor: 'pointer' }}
              >
                <div>
                  <div className="report-title">گزارش جامع</div>
                  <div className="report-desc">
                    تمام آمار در یک فایل
                  </div>
                </div>
                <button
                  className="btn-success btn-sm"
                  type="button"
                >
                  <i className="fas fa-download" />
                </button>
              </div>

              <div
                className="report-download"
                onClick={handleDownloadExams}
                role="button"
                tabIndex={0}
                style={{ cursor: 'pointer' }}
              >
                <div>
                  <div className="report-title">
                    گزارش آزمون‌ها
                  </div>
                  <div className="report-desc">
                    {toPersianDigits(stats.examCount)} آزمون
                    ثبت‌شده
                  </div>
                </div>
                <button
                  className="btn-success btn-sm"
                  type="button"
                >
                  <i className="fas fa-download" />
                </button>
              </div>

              <div
                className="report-download"
                onClick={handleDownloadStudy}
                role="button"
                tabIndex={0}
                style={{ cursor: 'pointer' }}
              >
                <div>
                  <div className="report-title">
                    گزارش مطالعه
                  </div>
                  <div className="report-desc">
                    {formatMinutesLong(stats.totalMin)} کل
                    مطالعه
                  </div>
                </div>
                <button
                  className="btn-success btn-sm"
                  type="button"
                >
                  <i className="fas fa-download" />
                </button>
              </div>

              <div className="rank-info">
                <span>
                  <i className="fas fa-trophy" />{' '}
                  {stats.rank
                    ? `رتبه: #${toPersianDigits(
                        stats.rank
                      )} از ${toPersianDigits(
                        stats.totalFriends
                      )}`
                    : 'هنوز در گروهی نیست'}
                </span>
                <span>
                  <i className="fas fa-bolt" />{' '}
                  {toPersianDigits(stats.xp)} XP
                </span>
              </div>
            </Card>
          </div>*/}

          {/* ===== ردیف ۵: داشبورد کامل فرزند ===== */}
          <div
            className="row-grid"
            style={{ gridTemplateColumns: '1fr' }}
          >
            <ChildDashboardCard
              child={selected}
              navigate={navigate}
            />
          </div>

          {/* ===== حالت خالی کلی ===== */}
          {!hasAnyData && (
            <div
              className="greeting-banner parent-greeting"
              style={{ marginTop: 4 }}
            >
              <div>
                <div className="greeting-title">🌟 شروع کنید</div>
                <div className="greeting-sub">
                  هنوز داده‌ای از {selected.name} ثبت نشده. وقتی
                  شروع کند، همه چیز اینجا نمایش داده می‌شود.
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ===== Add Child Modal ===== */}
      <AddChildModal
        isOpen={addChildOpen}
        onClose={() => setAddChildOpen(false)}
        onAdd={(data) => {
          const ok = addChild(data);
          if (ok) showToast('فرزند افزوده شد ✓');
          else showToast('این فرزند قبلاً ثبت شده', 'error');
        }}
      />

      {/* ===== Toast ===== */}
      {toast && (
        <div
          key={toast.id}
          className={`pdb-toast pdb-toast--${toast.type}`}
          role="status"
        >
          <i
            className={`fas ${
              toast.type === 'error'
                ? 'fa-exclamation-circle'
                : 'fa-check-circle'
            }`}
          />
          <span>{toast.text}</span>
        </div>
      )}
    </div>
  );
};

/* ================================================================
   ۲۳. Page Wrapper با تم persist
   ================================================================ */
const ParentHome = () => {
const [isDark, setIsDark] = useState(() => {
  try {
    return localStorage.getItem('navbar2-drawer-dark') === '1';
  } catch {
    return false;
  }
});

// ذخیره در کلید مشترک Navbar2
useEffect(() => {
  try {
    localStorage.setItem('navbar2-drawer-dark', isDark ? '1' : '0');
  } catch {}
}, [isDark]);

// گوش دادن به تغییرات Navbar2 (storage بین تب‌ها + polling داخل همون تب)
useEffect(() => {
  const handleStorage = (e) => {
    if (e.key === 'navbar2-drawer-dark') {
      setIsDark(e.newValue === '1');
    }
  };
  window.addEventListener('storage', handleStorage);

  const interval = setInterval(() => {
    try {
      const current = localStorage.getItem('navbar2-drawer-dark') === '1';
      setIsDark((prev) => (prev !== current ? current : prev));
    } catch {}
  }, 500);

  return () => {
    window.removeEventListener('storage', handleStorage);
    clearInterval(interval);
  };
}, []);

  const toggleTheme = useCallback(() => setIsDark((v) => !v), []);

  return (
    <div
      className={`parent-home ${isDark ? 'dark-mode' : ''}`}
      style={{
        minHeight: '100vh',
        backgroundImage: isDark
          ? "url('/h-dark.jpg')"
          : "url('/h.jpg')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
      }}
    >
      <div style={{ height: '85px' }} />
      <div className="app-container">
        <main className="main-content">
          <ParentDashboard
            onThemeChange={toggleTheme}
            isDark={isDark}
          />
        </main>
      </div>
      <div style={{ height: '85px' }} />
    </div>
  );
};

export default ParentHome;